import React, { useState, useEffect, useCallback } from "react"
import { Cards, Card } from "../Cards"
import "./style.css"
import { projectsApi, API_HOST } from "../../services/api"

function imgSrc(path) {
  if (!path) return null
  if (path.startsWith("http")) return path
  return `${API_HOST}${path}`
}

function normalizeUrl(url) {
  if (!url) return ""
  const clean = String(url).trim()
  if (!clean) return ""
  if (clean.startsWith("http://") || clean.startsWith("https://")) return clean
  return `https://${clean}`
}

function richTextValue(val) {
  if (val == null) return ""
  if (typeof val === "string") return val

  if (typeof val === "object") {
    if (val.body && typeof val.body === "object") {
      if (typeof val.body.body === "string") return val.body.body
      if (typeof val.body.html === "string") return val.body.html
      if (typeof val.body.to_s === "string") return val.body.to_s
    }

    if (typeof val.body === "string") return val.body
    if (typeof val.html === "string") return val.html
    if (typeof val.to_s === "string") return val.to_s
  }

  return String(val)
}

function stripHtml(html) {
  if (!html) return ""

  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

const PORTFOLIO_CATEGORIES = [
  { label: "branding", titleobj: { en: "Branding", ar: "الهوية البصرية" } },
  { label: "development", titleobj: { en: "Development", ar: "التطوير" } },
]

const ImageGallery = ({ images, alt }) => {
  const [active, setActive] = useState(0)
  const [animating, setAnimating] = useState(false)
  const [direction, setDirection] = useState("next")

  const go = useCallback((idx) => {
    if (animating || idx === active) return

    setDirection(idx > active ? "next" : "prev")
    setAnimating(true)

    setTimeout(() => {
      setActive(idx)
      setAnimating(false)
    }, 320)
  }, [active, animating])

  const prev = () => go(active === 0 ? images.length - 1 : active - 1)
  const next = () => go(active === images.length - 1 ? 0 : active + 1)

  useEffect(() => {
    setActive(0)
  }, [images])

  if (!images?.length) return null

  return (
    <div className="pm-gallery">
      <div className="pm-gallery__hero">
        <div className={`pm-gallery__slide ${animating ? `pm-gallery__slide--exit-${direction}` : "pm-gallery__slide--enter"}`}>
          <img src={imgSrc(images[active])} alt={`${alt} ${active + 1}`} />
        </div>

        {images.length > 1 && (
          <span className="pm-gallery__counter">
            {active + 1} / {images.length}
          </span>
        )}

        {images.length > 1 && (
          <>
            <button className="pm-gallery__arrow pm-gallery__arrow--prev" onClick={prev} aria-label="Previous image">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <button className="pm-gallery__arrow pm-gallery__arrow--next" onClick={next} aria-label="Next image">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="pm-gallery__thumbs">
          {images.map((src, i) => (
            <button
              key={i}
              className={`pm-gallery__thumb ${i === active ? "pm-gallery__thumb--active" : ""}`}
              onClick={() => go(i)}
              aria-label={`View image ${i + 1}`}
            >
              <img src={imgSrc(src)} alt={`Thumbnail ${i + 1}`} />
            </button>
          ))}
        </div>
      )}

      {images.length > 1 && (
        <div className="pm-gallery__dots">
          {images.map((_, i) => (
            <button
              key={i}
              className={`pm-gallery__dot ${i === active ? "pm-gallery__dot--active" : ""}`}
              onClick={() => go(i)}
              aria-label={`Go to image ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const TypeBadgeIcon = ({ project }) => {
  const hasWeb = !!project.url
  const hasMobile = !!project.mobile_app_url

  if (hasWeb && hasMobile) return <span className="gallery-item-type-icon">🌐 📱</span>
  if (hasWeb) return <span className="gallery-item-type-icon">🌐</span>
  if (hasMobile) return <span className="gallery-item-type-icon">📱</span>

  return null
}

const PortfolioPage = ({ lang }) => {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedTab, setSelectedTab] = useState(0)
  const [selectedProject, setSelectedProject] = useState(null)
  const [fullProject, setFullProject] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)
  const [isOpening, setIsOpening] = useState(false)

  const currentLang = lang || "en"
  const isAr = currentLang === "ar"
  const selectedCategory = PORTFOLIO_CATEGORIES[selectedTab].label

  // FIX: Fetch once in useEffect, not directly inside render.
  useEffect(() => {
    let active = true
    setLoading(true)

    projectsApi.index()
      .then(data => {
        if (!active) return
        setProjects(Array.isArray(data) ? data : [])
        setLoading(false)
      })
      .catch(() => {
        if (!active) return
        setProjects([])
        setLoading(false)
      })

    return () => {
      active = false
    }
  }, [])

  const filteredProjects = projects.filter(p => p.category === selectedCategory)

  const t = project => {
    if (!project) return ""
    return isAr
      ? (project.title_ar || project.title_en)
      : (project.title_en || project.title_ar)
  }

  const serviceName = service => {
    if (!service) return ""

    return isAr
      ? (service.title_ar || service.title_en || service.service_type)
      : (service.title_en || service.title_ar || service.service_type)
  }

  const categoryLabel = project => {
    if (project.category === "development") {
      const hasWeb = !!project.url
      const hasMobile = !!project.mobile_app_url

      if (hasWeb && hasMobile) return isAr ? "موقع وتطبيق" : "Website and Mobile App Design & Development"
      if (hasWeb) return isAr ? "تصميم وتطوير موقع" : "Website Design & Development"
      if (hasMobile) return isAr ? "تطبيق جوال" : "Mobile App Design & Development"

      return isAr ? "تطوير" : "Development"
    }

    return isAr ? "الهوية البصرية" : "Branding"
  }

  function hasValue(value) {
    return value !== undefined && value !== null && String(value).trim() !== ""
  }

  const getProjectDescription = project => {
    if (!project) return ""

    const htmlAr =
      richTextValue(project.description_ar_html) ||
      richTextValue(project.description_ar)

    const htmlEn =
      richTextValue(project.description_en_html) ||
      richTextValue(project.description_en)

    const plainAr = project.description_ar_plain || stripHtml(htmlAr)
    const plainEn = project.description_en_plain || stripHtml(htmlEn)

    if (isAr) {
      return hasValue(htmlAr) ? htmlAr : plainAr
    }

    return hasValue(htmlEn) ? htmlEn : plainEn
  }

  const openModal = project => {
    setSelectedProject(project)
    setFullProject(null)
    setModalLoading(true)
    document.body.style.overflow = "hidden"

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsOpening(true)
      })
    })

    projectsApi.show(project.id)
      .then(data => {
        setFullProject(data)
        setModalLoading(false)
      })
      .catch(() => {
        setModalLoading(false)
      })
  }

  const closeModal = () => {
    setIsOpening(false)

    setTimeout(() => {
      setSelectedProject(null)
      setFullProject(null)
      document.body.style.overflow = "unset"
    }, 350)
  }

  useEffect(() => {
    const handleEsc = e => {
      if (e.key === "Escape" && selectedProject) closeModal()
    }

    window.addEventListener("keydown", handleEsc)
    return () => window.removeEventListener("keydown", handleEsc)
  }, [selectedProject])

    const modalProject = fullProject
      ? { ...selectedProject, ...fullProject }
      : selectedProject

    const modalDescription =
      getProjectDescription(modalProject) ||
      getProjectDescription(selectedProject)

  return (
    <div
      className={`portfolio-page ${isAr ? "portfolio-ar-font" : ""}`}
      dir={isAr ? "rtl" : "ltr"}
    >
      <div className="b-gallery-sorting-holder">
        <div className="container">
          <div className="row">
            <div className="col-xs-12 col-sm-12">
              <ul className="list-inline text-uppercase text-center b-items-sort b-isotope__filter">
                {PORTFOLIO_CATEGORIES.map(({ titleobj, label }, index) => (
                  <li key={label} className={selectedTab === index ? "selected" : ""}>
                    <span
                      className={`pointer ${selectedTab === index ? "text-dark" : ""}`}
                      onClick={() => setSelectedTab(index)}
                    >
                      {titleobj[currentLang]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="b-items-gallery-holder">
        <div className="container">
          {loading ? (
            <p style={{ textAlign: "center", color: "#999", padding: "60px 0" }}>
              {isAr ? "جارٍ التحميل..." : "Loading..."}
            </p>
          ) : filteredProjects.length === 0 ? (
            <p style={{ textAlign: "center", color: "#999", padding: "60px 0" }}>
              {isAr ? "لا توجد مشاريع بعد" : "No projects yet"}
            </p>
          ) : (
            <Cards gap="35px" cardWidth="340px">
              {filteredProjects.map(project => {
                const coverImg = project.image_urls?.[0]

                return (
                  <div
                    key={project.id}
                    className="portfolio-card-link"
                    onClick={() => openModal(project)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={e => e.key === "Enter" && openModal(project)}
                  >
                    <Card>
                      <div className="gallery-item-content">
                        <div className="gallery-item-img">
                          {coverImg && <img src={imgSrc(coverImg)} alt={t(project)} />}

                          {project.image_urls?.length > 1 && (
                            <span className="gallery-item-count">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <polyline points="21 15 16 10 5 21" />
                              </svg>
                              {project.image_urls.length}
                            </span>
                          )}

                          {project.category === "development" && (
                            <TypeBadgeIcon project={project} />
                          )}
                        </div>

                        <div className="gallery-item-info">
                          <p className="item-category">
                            {project.client_name || (isAr ? "مشروع" : "Project")}
                          </p>

                          <h3 className="item-title">
                            {project.category === "development"
                              ? categoryLabel(project)
                              : t(project)}
                          </h3>
                        </div>
                      </div>
                    </Card>
                  </div>
                )
              })}
            </Cards>
          )}
        </div>
      </div>

      {selectedProject && (
        <div
          className={`portfolio-modal-overlay ${isOpening ? "open" : "close"}`}
          onClick={closeModal}
        >
          <div
            className={`portfolio-modal ${isOpening ? "open" : "close"}`}
            onClick={e => e.stopPropagation()}
          >
            <button className="portfolio-modal-close" onClick={closeModal} aria-label="Close modal">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="portfolio-modal-content">
              {selectedProject.image_urls?.length > 0 && (
                <ImageGallery
                  images={selectedProject.image_urls}
                  alt={t(selectedProject)}
                />
              )}

              <div className={`pm-category-pill ${selectedProject.category === "development" ? "pm-category-pill--dev" : ""}`}>
                {selectedProject.category === "development" && (
                  <span style={{ marginRight: isAr ? 0 : 6, marginLeft: isAr ? 6 : 0 }}>
                    {!!selectedProject.url && !!selectedProject.mobile_app_url && "🌐 📱"}
                    {!!selectedProject.url && !selectedProject.mobile_app_url && "🌐"}
                    {!selectedProject.url && !!selectedProject.mobile_app_url && "📱"}
                  </span>
                )}
                {categoryLabel(selectedProject)}
              </div>

              <div className="portfolio-modal-text">
                {selectedProject.client_name && (
                  <span className="portfolio-modal-client">
                    {selectedProject.client_name}
                  </span>
                )}

                <h2 className="portfolio-modal-title">
                  {t(selectedProject)}
                </h2>

                <div className="portfolio-modal-links">
                  {selectedProject.url && (
                    <a
                      href={normalizeUrl(selectedProject.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pm-link pm-link--web"
                    >
                      <span className="pm-link__icon">🌐</span>
                      <span>{isAr ? "زيارة الموقع" : "Visit Website"}</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </a>
                  )}

                  {selectedProject.mobile_app_url && (
                    <a
                      href={normalizeUrl(selectedProject.mobile_app_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pm-link pm-link--mobile"
                    >
                      <span className="pm-link__icon">📱</span>
                      <span>{isAr ? "عرض التطبيق" : "View Mobile App"}</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </a>
                  )}
                </div>

                {modalLoading && (
                  <p style={{ color: "#aaa", fontSize: 14 }}>
                    {isAr ? "جارٍ التحميل..." : "Loading..."}
                  </p>
                )}

                {modalDescription ? (
                  <div
                    className="portfolio-modal-description"
                    dangerouslySetInnerHTML={{ __html: modalDescription }}
                  />
                ) : !modalLoading ? (
                  <p className="portfolio-modal-description" style={{ color: "#999", fontStyle: "italic" }}>
                    {isAr ? "لا يوجد وصف لهذا المشروع حالياً." : "No project description available yet."}
                  </p>
                ) : null}

                {selectedProject.services?.length > 0 && (
                  <div style={{ marginTop: 20, display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {selectedProject.services.map(s => (
                      <span
                        key={s.id}
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600,
                          background: "#f0f4e0",
                          color: "#5a6f1f",
                          textTransform: "capitalize",
                        }}
                      >
                        {serviceName(s)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PortfolioPage
